import { useState, useEffect, useCallback, useRef } from 'react';
import type { Product, CatalogFilters, PaginationMeta } from '../types/product';
import { fetchProducts, fetchAllProducts } from '../services/productsApi';
import { API_CONFIG } from '../services/config';

// ─── Paginated hook — for catalog page (server-side pagination) ───────────────
interface UsePaginatedProductsResult {
  products: Product[];
  pagination: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isLive: boolean;
}

export function usePaginatedProducts(
  filters: CatalogFilters = {}
): UsePaginatedProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await fetchProducts(filtersRef.current);
      setProducts(result.data);
      setPagination(result.pagination);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar productos');
      console.error('[usePaginatedProducts]', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Re-fetch whenever key filter values change
  useEffect(() => {
    load();
  }, [
    load,
    filters.page,
    filters.pageSize,
    filters.globalFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.sorts),
  ]);

  return {
    products,
    pagination,
    loading,
    error,
    refresh: () => load(),
    lastUpdated,
    isLive: true,
  };
}

// ─── Flat hook — for home page sections (latest products, etc.) ───────────────
interface UseProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isLive: boolean;
}

export function useProducts(
  filters?: Omit<CatalogFilters, 'page' | 'pageSize'>
): UseProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchAllProducts(filtersRef.current);
      setProducts(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar productos');
      console.error('[useProducts]', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (API_CONFIG.POLLING_INTERVAL > 0) {
      intervalRef.current = setInterval(() => load(true), API_CONFIG.POLLING_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load]);

  return {
    products,
    loading,
    error,
    refresh: () => load(),
    lastUpdated,
    isLive: true,
  };
}